import './styles/app.css';
import {
  COMPANY_LOGO_SRC,
  COMPANY_MARK_SRC,
  COMPANY_NAME,
  COMPANY_URL,
  FORMAT_VERSION,
  SITE_DESCRIPTION,
  SITE_TITLE,
  SOFTWARE_NAME,
} from './config.ts';
import { detectFeatures } from './utils/feature-detect.ts';
import {
  defaultDecryptedFilename,
  defaultEncryptedFilename,
  triggerBlobDownload,
  zerocryptBlob,
} from './utils/download.ts';
import { createWriteSink } from './utils/fs-write.ts';
import {
  estimatePasswordStrength,
  generatePassphrase,
} from './utils/password.ts';
import { createCryptoWorker, type CryptoWorkerClient } from './workers/client.ts';
import {
  decryptFromRecipientText,
  defaultPqEncryptedFilename,
  encryptBytesToRecipient,
  encryptToRecipient,
  generateRecipientKeys,
  decryptBytesFromRecipient,
  PQ_FILE_EXTENSION,
  PQ_FILE_MIME,
} from './crypto/pq-recipient.ts';
import {
  fieldWithCopy,
  hasTextContent,
  refreshAutoGrow,
  wireAutoGrow,
  wireCopyButtons,
} from './ui/fields.ts';
import { CHUNK_SIZE_DEFAULT } from './crypto/format.ts';

document.title = SITE_TITLE;

const app = document.querySelector<HTMLDivElement>('#app')!;
const features = detectFeatures();

const brandMarkHtml = `
  <img
    class="brand-mark"
    src="${COMPANY_MARK_SRC}"
    width="40"
    height="40"
    alt="${COMPANY_NAME}"
    draggable="false"
    decoding="async"
  />`;

const footerHtml = `
  <footer class="site-footer">
    <div class="footer-inner">
      <a class="footer-brand" href="${COMPANY_URL}" target="_blank" rel="noopener noreferrer">
        <img
          class="footer-logo"
          src="${COMPANY_LOGO_SRC}"
          width="48"
          height="48"
          alt="${COMPANY_NAME} logo"
          draggable="false"
          decoding="async"
        />
        <div class="footer-brand-text">
          <p class="footer-company">Made by ${COMPANY_NAME}</p>
          <p class="footer-product">${SOFTWARE_NAME} · local encryption</p>
        </div>
      </a>
      <details class="footer-about">
        <summary>About</summary>
        <div class="footer-about-body">
          <p>
            <strong>${SOFTWARE_NAME}</strong> is a privacy-first encryptor by
            <a href="${COMPANY_URL}" target="_blank" rel="noopener noreferrer">${COMPANY_NAME}</a>.
            Files, text, passwords, and keys stay in your browser — nothing is uploaded for
            encryption or decryption.
          </p>
          <p>
            Password mode uses Argon2id and AES-256-GCM. The optional quantum-resistance
            mode is experimental hybrid recipient encryption (ML-KEM-768 + X25519) and is
            not marketed as quantum-proof.
          </p>
          <p>
            <a href="${COMPANY_URL}" target="_blank" rel="noopener noreferrer">Visit ${COMPANY_NAME} →</a>
          </p>
        </div>
      </details>
    </div>
  </footer>`;

if (!features.ok) {
  app.innerHTML = `
    <nav class="navbar" aria-label="Primary">
      <div class="navbar-inner">
        <div class="brand-lockup">
          ${brandMarkHtml}
          <h1 class="brand-3d">${SOFTWARE_NAME}<span class="sr-only"> — local file and text encryption</span></h1>
        </div>
      </div>
    </nav>
    <div class="unsupported" role="alert">
      <p><strong>Unsupported browser</strong></p>
      <p>Required APIs are missing: ${features.missing.join(', ')}.</p>
      <p>ZeroCrypt will not silently weaken cryptography. Please use a current Chrome, Edge, Firefox, or Safari.</p>
    </div>
    ${footerHtml}`;
} else {
  mountApp();
}

function mountApp(): void {
  const worker = createCryptoWorker();
  let selectedFile: File | null = null;
  let pqSelectedFile: File | null = null;
  let cryptoMode: 'normal' | 'pq' = 'normal';
  let contentMode: 'files' | 'text' = 'files';
  let pqContentMode: 'files' | 'text' = 'files';
  let action: 'encrypt' | 'decrypt' = 'encrypt';

  app.innerHTML = `
    <nav class="navbar" aria-label="Primary">
      <div class="navbar-inner">
        <div class="brand-lockup">
          ${brandMarkHtml}
          <h1 class="brand-3d">${SOFTWARE_NAME}<span class="sr-only"> — local file and text encryption</span></h1>
        </div>
        <div class="nav-modes" role="tablist" aria-label="Encryption mode">
          <button type="button" class="nav-mode" role="tab" data-crypto="normal" aria-selected="true">
            Normal Encryption
          </button>
          <button type="button" class="nav-mode" role="tab" data-crypto="pq" aria-selected="false">
            Quantum Resistance <span class="badge">Experimental</span>
          </button>
        </div>
      </div>
    </nav>

    <main class="page">
      <p class="sr-only">${SITE_DESCRIPTION}</p>
      <div class="workspace">
        <div class="seg" role="tablist" aria-label="Encrypt or decrypt">
          <button type="button" class="seg-btn" role="tab" data-action="encrypt" aria-selected="true">Encrypt</button>
          <button type="button" class="seg-btn" role="tab" data-action="decrypt" aria-selected="false">Decrypt</button>
        </div>

        <!-- Normal password mode -->
        <section id="view-normal" class="crypto-view active" aria-label="Normal encryption">
          <div class="section-title">
            <h2 id="normal-heading">Encrypt files or text</h2>
            <p>Password-based AES-256-GCM · Argon2id · processed on this device</p>
          </div>

          <div class="chip-row" role="tablist" aria-label="Content type">
            <button type="button" class="chip" role="tab" data-content="files" aria-selected="true">Files</button>
            <button type="button" class="chip" role="tab" data-content="text" aria-selected="false">Text</button>
          </div>

          <div id="panel-files" class="panel active">
            <div class="dropzone" id="dropzone" tabindex="0" role="button" aria-label="Drop a file here or press Enter to choose">
              <p class="dropzone-title">Drop a file here</p>
              <p class="dropzone-sub">or choose from your device</p>
              <button type="button" class="btn" id="choose-file">Choose File</button>
              <p class="file-name" id="file-label">No file selected</p>
              <input type="file" id="file-input" class="sr-only" />
            </div>
          </div>

          <div id="panel-text" class="panel">
            <div id="text-encrypt-fields">
              ${fieldWithCopy({
                id: 'text-plain',
                label: 'Plaintext',
                placeholder: 'Type or paste text…',
              })}
            </div>
            <div id="text-decrypt-fields" hidden>
              ${fieldWithCopy({
                id: 'text-cipher',
                label: 'Encrypted text (ZC1:…)',
                placeholder: 'Paste ZC1:… payload…',
              })}
            </div>
            <div class="actions" id="text-actions">
              <button type="button" class="btn btn-ghost" id="download-text-out" hidden>Download encrypted text</button>
            </div>
          </div>

          <div class="actions actions-top" id="gen-pw-wrap">
            <button type="button" class="btn" id="gen-pw" title="Generate a high-entropy passphrase">Generate passphrase</button>
          </div>

          <div class="pw-fields">
            <div class="field" id="password-block">
              <label for="password">Password</label>
              <div class="pw-input">
                <input id="password" type="password" autocomplete="new-password" />
                <button type="button" class="icon-btn pw-toggle" id="toggle-pw" aria-pressed="false" aria-label="Show password" title="Show password">
                  <svg class="eye-open" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg class="eye-off" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
              <div class="strength" id="strength" aria-live="polite"></div>
            </div>

            <div class="field" id="confirm-block">
              <label for="confirm">Confirm password</label>
              <div class="pw-input">
                <input id="confirm" type="password" autocomplete="new-password" />
                <button type="button" class="icon-btn pw-toggle" id="toggle-confirm" aria-pressed="false" aria-label="Show confirm password" title="Show password">
                  <svg class="eye-open" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg class="eye-off" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
            </div>
          </div>

          <p class="warning" id="loss-warning">
            If you lose this password, the encrypted content cannot be recovered. There is no reset and no backdoor.
          </p>

          <div class="actions">
            <button type="button" class="btn btn-primary" id="run-btn">Encrypt</button>
            <button type="button" class="btn btn-danger" id="cancel-btn" disabled>Cancel</button>
          </div>
          <p class="action-hint" id="normal-action-hint"></p>

          <div class="progress" id="progress" aria-live="polite">
            <div class="progress-bar"><span id="progress-fill"></span></div>
            <div class="progress-meta" id="progress-meta">Starting…</div>
          </div>

          <p class="hint">Processed entirely on this device · Server upload: none</p>

          <details class="details">
            <summary>Encryption details</summary>
            <pre>Encryption: AES-256-GCM
Password KDF: Argon2id (m=65536 KiB, t=3, p=1)
Key model: Envelope (random CEK wrapped by password-derived KEK)
Processing: Local browser (Web Worker)
Server upload: None
Format version: ${FORMAT_VERSION}
Chunk size: ${CHUNK_SIZE_DEFAULT} bytes
Extension: .zerocrypt</pre>
          </details>
        </section>

        <!-- Quantum-resistance experimental mode -->
        <section id="view-pq" class="crypto-view" aria-label="Quantum resistance experimental">
          <div class="section-title">
            <h2 id="pq-heading">Hybrid post-quantum recipient</h2>
            <p>ML-KEM-768 + X25519 · experimental · not “quantum-proof”</p>
          </div>
          <p class="pq-note">
            Encrypt text or files to someone’s public key. Decryption uses the matching secret key.
            Everything stays in this browser.
          </p>

          <div class="actions actions-top">
            <button type="button" class="btn" id="pq-gen">Generate key pair</button>
          </div>

          <div class="chip-row" role="tablist" aria-label="PQ content type">
            <button type="button" class="chip" role="tab" data-pq-content="files" aria-selected="true">Files</button>
            <button type="button" class="chip" role="tab" data-pq-content="text" aria-selected="false">Text</button>
          </div>

          <div id="pq-panel-files" class="panel active">
            <div class="dropzone" id="pq-dropzone" tabindex="0" role="button" aria-label="Drop a file here or press Enter to choose">
              <p class="dropzone-title">Drop a file here</p>
              <p class="dropzone-sub">or choose from your device</p>
              <button type="button" class="btn" id="pq-choose-file">Choose File</button>
              <p class="file-name" id="pq-file-label">No file selected</p>
              <input type="file" id="pq-file-input" class="sr-only" />
            </div>
          </div>

          <div id="pq-encrypt-fields">
            ${fieldWithCopy({
              id: 'pq-pub',
              label: 'Recipient public key',
              placeholder: 'ZCPK1:…',
            })}
            <div id="pq-msg-wrap">
              ${fieldWithCopy({
                id: 'pq-msg',
                label: 'Message',
                placeholder: 'Text to encrypt…',
                copy: false,
              })}
            </div>
          </div>

          <div id="pq-decrypt-fields" hidden>
            ${fieldWithCopy({
              id: 'pq-sec',
              label: 'Your secret key',
              placeholder: 'ZCSK1:…',
            })}
            <div id="pq-cipher-wrap">
              ${fieldWithCopy({
                id: 'pq-cipher',
                label: 'Encrypted payload',
                placeholder: 'ZC-PQ1:…',
              })}
            </div>
          </div>

          <div class="actions">
            <button type="button" class="btn btn-primary" id="pq-run">Encrypt</button>
          </div>
          <p class="action-hint" id="pq-action-hint"></p>

          <div id="pq-out-wrap">
            ${fieldWithCopy({
              id: 'pq-out',
              label: 'Result',
              placeholder: 'Output appears here…',
              readonly: true,
            })}
          </div>
        </section>

        <p class="status" id="status" role="status"></p>
      </div>

      <div class="channel-tip" role="note">
        <strong>Safer sharing tip.</strong>
        <span>
          Send the encrypted file or payload on one channel, and the password or secret key on a
          <em>different</em> channel (for example email + messaging app). Never put the secret
          together with the ciphertext. Public keys are safe to share openly.
        </span>
      </div>

      <div class="privacy-banner" role="note">
        <strong>Your files never leave your device.</strong>
        <span>Encryption runs locally in your browser. Passwords and keys are never transmitted.</span>
      </div>
    </main>
    ${footerHtml}
  `;

  const $ = <T extends HTMLElement>(sel: string) => app.querySelector<T>(sel)!;

  const dropzone = $('#dropzone');
  const fileInput = $<HTMLInputElement>('#file-input');
  const fileLabel = $('#file-label');
  const password = $<HTMLInputElement>('#password');
  const confirm = $<HTMLInputElement>('#confirm');
  const strengthEl = $('#strength');
  const statusEl = $('#status');
  const runBtn = $<HTMLButtonElement>('#run-btn');
  const cancelBtn = $<HTMLButtonElement>('#cancel-btn');
  const progress = $('#progress');
  const progressFill = $('#progress-fill');
  const progressMeta = $('#progress-meta');
  const textPlain = $<HTMLTextAreaElement>('#text-plain');
  const textCipher = $<HTMLTextAreaElement>('#text-cipher');
  const textEncryptFields = $('#text-encrypt-fields');
  const textDecryptFields = $('#text-decrypt-fields');
  const confirmBlock = $('#confirm-block');
  const genPwWrap = $('#gen-pw-wrap');
  const lossWarning = $('#loss-warning');
  const downloadTextOut = $<HTMLButtonElement>('#download-text-out');
  const normalHeading = $('#normal-heading');
  const normalActionHint = $('#normal-action-hint');
  const pqActionHint = $('#pq-action-hint');
  const pqRun = $<HTMLButtonElement>('#pq-run');
  const pqEncryptFields = $('#pq-encrypt-fields');
  const pqDecryptFields = $('#pq-decrypt-fields');
  const pqPub = $<HTMLTextAreaElement>('#pq-pub');
  const pqMsg = $<HTMLTextAreaElement>('#pq-msg');
  const pqSec = $<HTMLTextAreaElement>('#pq-sec');
  const pqCipher = $<HTMLTextAreaElement>('#pq-cipher');
  const pqOut = $<HTMLTextAreaElement>('#pq-out');
  const pqMsgWrap = $('#pq-msg-wrap');
  const pqCipherWrap = $('#pq-cipher-wrap');
  const pqOutWrap = $('#pq-out-wrap');
  const pqDropzone = $('#pq-dropzone');
  const pqFileInput = $<HTMLInputElement>('#pq-file-input');
  const pqFileLabel = $('#pq-file-label');
  const pqPanelFiles = $('#pq-panel-files');

  function setStatus(msg: string, kind: '' | 'ok' | 'error' = ''): void {
    statusEl.textContent = msg;
    statusEl.className = `status${kind ? ` ${kind}` : ''}`;
  }

  function updateStrength(): void {
    const { level, hints } = estimatePasswordStrength(password.value);
    strengthEl.className = `strength ${level}`;
    strengthEl.textContent =
      password.value.length === 0
        ? ''
        : `Strength: ${level.replace('-', ' ')}${hints.length ? ` — ${hints.join('; ')}` : ''}`;
  }

  function updateActionHints(): void {
    const encrypting = action === 'encrypt';
    if (encrypting) {
      normalActionHint.textContent =
        contentMode === 'files'
          ? 'After encrypting: share the .zerocrypt file and the password separately. The other side needs both to decrypt.'
          : 'After encrypting: copy or download the ZC1: text and share the password separately. The other side needs both to decrypt.';
      pqActionHint.textContent =
        pqContentMode === 'files'
          ? `After encrypting: send the ${PQ_FILE_EXTENSION} file to the recipient. They decrypt with their secret key — never send the secret key with the file.`
          : 'After encrypting: send the ZC-PQ1: result to the recipient. They decrypt with their secret key — never share secret keys.';
    } else {
      normalActionHint.textContent =
        contentMode === 'files'
          ? 'To decrypt: choose the .zerocrypt file and enter the same password used when it was encrypted.'
          : 'To decrypt: paste the ZC1: payload and enter the same password used when it was encrypted.';
      pqActionHint.textContent =
        pqContentMode === 'files'
          ? `To decrypt: choose the ${PQ_FILE_EXTENSION} file and paste your secret key (ZCSK1:).`
          : 'To decrypt: paste your secret key (ZCSK1:) and the ZC-PQ1: payload you received from the sender.';
    }
  }

  function syncActionUi(): void {
    const encrypting = action === 'encrypt';

    app.querySelectorAll('[data-action]').forEach((el) => {
      el.setAttribute('aria-selected', String(el.getAttribute('data-action') === action));
    });

    runBtn.textContent =
      contentMode === 'files'
        ? encrypting
          ? 'Encrypt file'
          : 'Decrypt file'
        : encrypting
          ? 'Encrypt text'
          : 'Decrypt text';
    confirmBlock.hidden = !encrypting;
    genPwWrap.hidden = !encrypting;
    lossWarning.hidden = !encrypting;
    normalHeading.textContent = encrypting ? 'Encrypt files or text' : 'Decrypt files or text';
    textEncryptFields.hidden = !encrypting;
    textDecryptFields.hidden = encrypting;
    downloadTextOut.hidden = encrypting || !textCipher.value.trim();

    pqEncryptFields.hidden = !encrypting;
    pqDecryptFields.hidden = encrypting;
    pqMsgWrap.hidden = pqContentMode !== 'text' || !encrypting;
    pqCipherWrap.hidden = pqContentMode !== 'text' || encrypting;
    pqOutWrap.hidden = pqContentMode !== 'text';
    pqPanelFiles.classList.toggle('active', pqContentMode === 'files');
    pqRun.textContent =
      pqContentMode === 'files'
        ? encrypting
          ? 'Encrypt file to public key'
          : 'Decrypt file with secret key'
        : encrypting
          ? 'Encrypt text to public key'
          : 'Decrypt text with secret key';

    updateActionHints();
    refreshAutoGrow(app);
  }

  function preferredActionForMode(mode: 'normal' | 'pq'): 'encrypt' | 'decrypt' {
    if (mode === 'pq') {
      if (hasTextContent(pqSec, pqCipher)) return 'decrypt';
      if (pqSelectedFile?.name.toLowerCase().endsWith(PQ_FILE_EXTENSION)) return 'decrypt';
      return 'encrypt';
    }
    return hasTextContent(textCipher) ? 'decrypt' : 'encrypt';
  }

  function setCryptoMode(next: 'normal' | 'pq'): void {
    cryptoMode = next;
    app.querySelectorAll('[data-crypto]').forEach((el) => {
      el.setAttribute('aria-selected', String(el.getAttribute('data-crypto') === next));
    });
    $('#view-normal').classList.toggle('active', next === 'normal');
    $('#view-pq').classList.toggle('active', next === 'pq');
    setStatus('');
    setAction(preferredActionForMode(next));
  }

  function setContentMode(next: 'files' | 'text'): void {
    contentMode = next;
    app.querySelectorAll('[data-content]').forEach((el) => {
      el.setAttribute('aria-selected', String(el.getAttribute('data-content') === next));
    });
    $('#panel-files').classList.toggle('active', next === 'files');
    $('#panel-text').classList.toggle('active', next === 'text');
    syncActionUi();
  }

  function setPqContentMode(next: 'files' | 'text'): void {
    pqContentMode = next;
    app.querySelectorAll('[data-pq-content]').forEach((el) => {
      el.setAttribute('aria-selected', String(el.getAttribute('data-pq-content') === next));
    });
    syncActionUi();
  }

  function setAction(next: 'encrypt' | 'decrypt'): void {
    action = next;
    syncActionUi();
  }

  function setFile(file: File | null): void {
    selectedFile = file;
    fileLabel.textContent = file
      ? `${file.name} (${formatBytes(file.size)})`
      : 'No file selected';
  }

  function setPqFile(file: File | null): void {
    pqSelectedFile = file;
    pqFileLabel.textContent = file
      ? `${file.name} (${formatBytes(file.size)})`
      : 'No file selected';
  }

  function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
  }

  function showProgress(visible: boolean): void {
    progress.classList.toggle('visible', visible);
    cancelBtn.disabled = !visible;
    runBtn.disabled = visible;
  }

  app.querySelectorAll('[data-crypto]').forEach((btn) => {
    btn.addEventListener('click', () =>
      setCryptoMode(btn.getAttribute('data-crypto') as 'normal' | 'pq'),
    );
  });
  app.querySelectorAll('[data-action]').forEach((btn) => {
    btn.addEventListener('click', () =>
      setAction(btn.getAttribute('data-action') as 'encrypt' | 'decrypt'),
    );
  });
  app.querySelectorAll('[data-content]').forEach((btn) => {
    btn.addEventListener('click', () =>
      setContentMode(btn.getAttribute('data-content') as 'files' | 'text'),
    );
  });
  app.querySelectorAll('[data-pq-content]').forEach((btn) => {
    btn.addEventListener('click', () =>
      setPqContentMode(btn.getAttribute('data-pq-content') as 'files' | 'text'),
    );
  });

  $('#choose-file').addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    setFile(fileInput.files?.[0] ?? null);
  });

  dropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.classList.add('dragover');
  });
  dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'));
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('dragover');
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  });
  dropzone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInput.click();
    }
  });

  $('#pq-choose-file').addEventListener('click', () => pqFileInput.click());
  pqFileInput.addEventListener('change', () => {
    setPqFile(pqFileInput.files?.[0] ?? null);
  });
  pqDropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    pqDropzone.classList.add('dragover');
  });
  pqDropzone.addEventListener('dragleave', () => pqDropzone.classList.remove('dragover'));
  pqDropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    pqDropzone.classList.remove('dragover');
    const f = e.dataTransfer?.files?.[0];
    if (f) setPqFile(f);
  });
  pqDropzone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pqFileInput.click();
    }
  });

  password.addEventListener('input', updateStrength);

  function setPwVisibility(
    input: HTMLInputElement,
    toggle: HTMLButtonElement,
    visible: boolean,
  ): void {
    input.type = visible ? 'text' : 'password';
    toggle.setAttribute('aria-pressed', String(visible));
    toggle.setAttribute('aria-label', visible ? 'Hide password' : 'Show password');
    toggle.title = visible ? 'Hide password' : 'Show password';
  }

  $('#toggle-pw').addEventListener('click', () => {
    const show = password.type === 'password';
    setPwVisibility(password, $('#toggle-pw'), show);
  });
  $('#toggle-confirm').addEventListener('click', () => {
    const show = confirm.type === 'password';
    setPwVisibility(confirm, $('#toggle-confirm'), show);
  });
  $('#gen-pw').addEventListener('click', () => {
    const pw = generatePassphrase(32);
    password.value = pw;
    confirm.value = pw;
    setPwVisibility(password, $('#toggle-pw'), true);
    setPwVisibility(confirm, $('#toggle-confirm'), true);
    updateStrength();
    setStatus(
      'Generated a high-entropy passphrase. Store it safely — it cannot be recovered.',
      'ok',
    );
  });

  cancelBtn.addEventListener('click', () => {
    worker.cancel();
    showProgress(false);
    setStatus('Cancelled.', 'error');
  });

  runBtn.addEventListener('click', () => void runNormal(worker));

  $('#pq-gen').addEventListener('click', () => {
    const keys = generateRecipientKeys();
    pqPub.value = keys.publicKey;
    pqSec.value = keys.secretKey;
    refreshAutoGrow(app);
    setStatus('Generated ML-KEM-768 + X25519 hybrid recipient key pair locally.', 'ok');
  });

  pqRun.addEventListener('click', () => {
    void (async () => {
      setStatus('');
      try {
        if (pqContentMode === 'files') {
          if (!pqSelectedFile) {
            setStatus('Choose a file first.', 'error');
            return;
          }
          const file = pqSelectedFile;
          if (action === 'encrypt') {
            if (!pqPub.value.trim()) {
              setStatus('Enter the recipient public key.', 'error');
              return;
            }
            const plain = new Uint8Array(await file.arrayBuffer());
            const body = await encryptBytesToRecipient(pqPub.value, plain);
            const name = defaultPqEncryptedFilename(file.name);
            const copy = new Uint8Array(body.byteLength);
            copy.set(body);
            triggerBlobDownload(new Blob([copy], { type: PQ_FILE_MIME }), name);
            setStatus(`Encrypted locally → ${name}`, 'ok');
          } else {
            if (!pqSec.value.trim()) {
              setStatus('Enter your secret key.', 'error');
              return;
            }
            const body = new Uint8Array(await file.arrayBuffer());
            const plain = await decryptBytesFromRecipient(pqSec.value, body);
            const base = file.name.endsWith(PQ_FILE_EXTENSION)
              ? file.name.slice(0, -PQ_FILE_EXTENSION.length)
              : file.name.replace(/\.[^/.]+$/, '') || 'decrypted';
            const outName = base.includes('.') ? base : `${base}.bin`;
            const copy = new Uint8Array(plain.byteLength);
            copy.set(plain);
            triggerBlobDownload(new Blob([copy], { type: 'application/octet-stream' }), outName);
            setStatus(`Decrypted locally → ${outName}`, 'ok');
          }
          return;
        }

        if (action === 'encrypt') {
          const out = await encryptToRecipient(pqPub.value, pqMsg.value);
          pqOut.value = out;
          refreshAutoGrow(app);
          setStatus('Encrypted to recipient public key (hybrid PQ).', 'ok');
        } else {
          const text = await decryptFromRecipientText(pqSec.value, pqCipher.value);
          pqOut.value = text;
          refreshAutoGrow(app);
          setStatus('Decrypted with recipient secret key.', 'ok');
        }
      } catch (e) {
        setStatus((e as Error).message, 'error');
      }
    })();
  });

  downloadTextOut.addEventListener('click', () => {
    const blob = new Blob([textCipher.value], { type: 'text/plain;charset=utf-8' });
    triggerBlobDownload(blob, `text.zerocrypt.txt`);
  });

  wireAutoGrow(app);
  wireCopyButtons(app, () => setStatus('Copied to clipboard.', 'ok'));
  syncActionUi();

  async function runNormal(w: CryptoWorkerClient): Promise<void> {
    if (cryptoMode !== 'normal') return;
    setStatus('');
    const pw = password.value;
    if (!pw) {
      setStatus('Enter a password.', 'error');
      return;
    }
    if (action === 'encrypt' && pw !== confirm.value) {
      setStatus('Passwords do not match.', 'error');
      return;
    }

    showProgress(true);
    progressFill.style.width = '0%';
    progressMeta.textContent = 'Deriving key (Argon2id)…';

    try {
      if (contentMode === 'text') {
        if (action === 'encrypt') {
          const payload = await w.encryptText({
            password: pw,
            text: textPlain.value,
          });
          textCipher.value = payload;
          downloadTextOut.hidden = false;
          setAction('decrypt');
          refreshAutoGrow(app);
          setStatus('Text encrypted locally. Copy or download the ZC1: payload.', 'ok');
        } else {
          const text = await w.decryptText({
            password: pw,
            payload: textCipher.value,
          });
          textPlain.value = text;
          setAction('encrypt');
          refreshAutoGrow(app);
          setStatus('Text decrypted locally.', 'ok');
        }
      } else {
        if (!selectedFile) {
          setStatus('Choose a file first.', 'error');
          showProgress(false);
          return;
        }
        const file = selectedFile;
        if (action === 'encrypt') {
          const container = await w.encryptFile({
            password: pw,
            file,
            metadata: {
              name: file.name,
              mime: file.type || 'application/octet-stream',
              created: new Date().toISOString(),
            },
            onProgress: (p) => {
              progressFill.style.width = `${p.percent}%`;
              progressMeta.textContent =
                p.phase === 'kdf'
                  ? 'Deriving key (Argon2id)…'
                  : `Encrypting… ${formatBytes(p.bytesProcessed)} / ${formatBytes(p.bytesTotal)} (${p.percent}%)`;
            },
          });

          const name = defaultEncryptedFilename(file.name);
          const { sink, mode: writeMode } = await createWriteSink(name);
          try {
            await sink.write(container);
            await sink.close();
            if (writeMode === 'blob') {
              const blob = sink.resultBlob?.() ?? zerocryptBlob(container);
              triggerBlobDownload(blob, name);
            }
          } catch (err) {
            await sink.abort();
            throw err;
          }
          setStatus(`Encrypted locally → ${name}`, 'ok');
        } else {
          const buffer = new Uint8Array(await file.arrayBuffer());
          const result = await w.decrypt({
            password: pw,
            container: buffer,
            onProgress: (p) => {
              progressFill.style.width = `${p.percent}%`;
              progressMeta.textContent =
                p.phase === 'kdf'
                  ? 'Deriving key (Argon2id)…'
                  : `Decrypting… ${formatBytes(p.bytesProcessed)} / ${formatBytes(p.bytesTotal)} (${p.percent}%)`;
            },
          });
          const outName = defaultDecryptedFilename(result.metadata.name);
          const copy = new Uint8Array(result.plaintext.byteLength);
          copy.set(result.plaintext);
          const blob = new Blob([copy], {
            type: result.metadata.mime || 'application/octet-stream',
          });
          triggerBlobDownload(blob, outName);
          setStatus(`Decrypted locally → ${outName}`, 'ok');
        }
      }
    } catch (e) {
      const err = e as Error;
      if (err.name === 'AbortError') {
        setStatus('Cancelled.', 'error');
      } else {
        setStatus(err.message || 'Operation failed', 'error');
      }
    } finally {
      showProgress(false);
    }
  }
}
